import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, Handshake, Target, TrendingUp, ArrowRight } from 'lucide-react';
import { PageHero, PageWrapper, SectionHeader, Card, StatCard } from '../components/UI';

const stages = [
  {
    stage: 'Stage 1',
    title: 'Roll Out & Operate',
    period: '2026-2032',
    capex: '-',
    margin: '15-25% (fleet/sales)',
    revenue: '$1M -> $263M',
    text: 'text-accent-green',
    border: 'border-accent-green',
    borderSoft: 'border-accent-green/20',
    bgSoft: 'bg-accent-green/10',
    activeBg: 'bg-accent-green/10',
    bar: 'bg-accent-green',
    desc: 'Azura operates as a fleet and operations company, rolling out electric motorcycles, building a BaaS network, launching ride-hailing, and expanding across Nigeria. No manufacturing investment required.',
    focus: 'Build brand, validate model, generate cash, develop talent pipeline via EV Academy',
    capital: 'AZT token raises + operating revenue',
    partners: ['State government', 'Battery supplier', 'Fleet technology partner'],
    outcome: 'Estimated cumulative revenue: $50M-$200M funding Stage 2',
  },
  {
    stage: 'Stage 2',
    title: 'Local Assembly',
    period: '2033-2037',
    capex: '$20M-$50M',
    margin: '15-25%',
    revenue: '$200M-$500M',
    text: 'text-accent-blue',
    border: 'border-accent-blue',
    borderSoft: 'border-accent-blue/20',
    bgSoft: 'bg-accent-blue/10',
    activeBg: 'bg-accent-blue/10',
    bar: 'bg-accent-blue',
    desc: 'Azura establishes its first local assembly facility, importing vehicle parts kits from global suppliers and assembling them under the Azura brand in Nigeria.',
    focus: 'Produce first Azura-assembled vehicle in Nigeria, build local supplier relationships',
    capital: 'Stage 1 operating revenue + development finance',
    partners: ['Global vehicle parts kit supplier', 'Industrial development partner', 'State/Federal government'],
    outcome: 'First Azura-assembled vehicle produced in Nigeria',
  },
  {
    stage: 'Stage 3',
    title: 'Partial manufacturing',
    period: '2038-2041',
    capex: '$100M-$200M',
    margin: '30-40%',
    revenue: '$500M-$1.5B',
    text: 'text-accent-amber',
    border: 'border-accent-amber',
    borderSoft: 'border-accent-amber/20',
    bgSoft: 'bg-accent-amber/10',
    activeBg: 'bg-accent-amber/10',
    bar: 'bg-accent-amber',
    desc: 'Azura designs and owns its first vehicle components, specifically the drivetrain and battery management system, while continuing to source remaining components from global partners.',
    focus: 'First vehicle with Azura-designed drivetrain and battery technology',
    capital: 'Research and development and tooling investment + manufacturing partnerships',
    partners: ['Global vehicle-manufacturing technology partner', 'University research and development consortium', 'Battery technology partner'],
    outcome: 'Azura-owned technology in drivetrain and battery management system. Profit margin jumps to 30-40%.',
  },
  {
    stage: 'Stage 4',
    title: 'Full manufacturing',
    period: '2042-2045',
    capex: '$200M-$500M',
    margin: '40-55%',
    revenue: '$2B-$5B+',
    text: 'text-violet-400',
    border: 'border-violet-400',
    borderSoft: 'border-violet-400/20',
    bgSoft: 'bg-violet-400/10',
    activeBg: 'bg-violet-400/10',
    bar: 'bg-violet-400',
    desc: "Azura becomes a full vehicle manufacturer, owning the complete design, engineering, and production of its electric vehicles.",
    focus: 'Full design ownership, export capability, continental manufacturing leadership',
    capital: 'Full manufacturing buildout - continental and global ambition',
    partners: ['Global vehicle-manufacturing strategic partner', 'Export market distribution partners', 'Industrial development bank'],
    outcome: "Africa's first local EV manufacturer. Export-capable. $2B-$5B+ annual manufacturing revenue.",
  },
];

const Manufacturing: React.FC = () => {
  const [activeStage, setActiveStage] = useState(0);
  const s = stages[activeStage];

  return (
    <>
      <PageHero
        eyebrow="- MANUFACTURING ROADMAP"
        title="From Fleet Operator to Africa's First Manufacturer"
        subtitle="Azura's manufacturing vision transforms the company from a transportation services company into a continental industrial platform - the BYD playbook applied to Africa."
        accent="#a78bfa"
      />
      <PageWrapper>
        <div className="mb-20 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-5">
          <StatCard value="2033" label="Local Assembly Launch" sub="First locally built Azura" accent="#1eadff" />
          <StatCard value="2042" label="Full Manufacturing Achieved" sub="Africa's first local EV manufacturer" accent="#a78bfa" />
          <StatCard value="40-55%" label="Full Manufacturing Profit Margin" sub="vs 15-25% at fleet stage" accent="var(--accent-amber)" />
          <StatCard value="$5B+" label="Manufacturing Revenue by 2045" sub="Annual manufacturing revenue" accent="var(--accent-green)" />
        </div>

        <div className="mb-16 rounded-[20px] border border-violet-400/20 bg-gradient-to-br from-violet-400/10 to-accent-blue/5 p-8">
          <div className="mb-3 flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.1em] text-violet-300">
            <Target size={16} /> Management Recommendation
          </div>
          <p className="text-base leading-7 text-text-primary">
            Azura's management recommends pursuing the phased assembly-to-manufacturing path rather than attempting full manufacturing from launch. Each stage pays for the next one itself, risk is managed step by step, and the EV Academy ensures the talent pipeline keeps pace with the manufacturing ambition. This is the <strong className="text-violet-300">BYD playbook applied to Africa</strong>.
          </p>
        </div>

        <div className="mb-16 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { src: '/images/bike6.png', label: 'Fleet & BaaS rollout' },
            { src: '/images/bg3.jpg', label: 'Local assembly' },
            { src: '/images/impact6.jpg', label: 'Full manufacturing scale' },
          ].map((img) => (
            <div key={img.src} className="relative rounded-2xl overflow-hidden aspect-[4/3] border border-white/10">
              <img src={img.src} alt={img.label} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <p className="absolute bottom-3 left-4 text-white text-xs uppercase tracking-widest">{img.label}</p>
            </div>
          ))}
        </div>

        <SectionHeader eyebrow="- FOUR STAGES" title="The Manufacturing Journey" />
        <div className="mb-8 flex flex-wrap gap-4">
          {stages.map((st, i) => (
            <button
              key={st.stage}
              onClick={() => setActiveStage(i)}
              className={`min-w-40 flex-1 cursor-pointer rounded-[14px] border-2 p-4 text-left transition duration-200 hover:-translate-y-1 hover:bg-bg-elevated ${activeStage === i ? `${st.border} ${st.activeBg}` : 'border-white/10 bg-bg-card'}`}
            >
              <p className={`mb-1 text-[0.65rem] ${st.text}`}>{st.period}</p>
              <p className="font-heading text-sm font-bold text-text-primary">{st.title}</p>
              <p className="mt-0.5 text-xs text-text-muted">Rev: {st.revenue}</p>
            </button>
          ))}
        </div>

        <Card className={`relative mb-16 overflow-hidden rounded-3xl border-2 p-10 ${s.borderSoft}`}>
          <div className={`absolute inset-x-0 top-0 h-[3px] ${s.bar}`} />
          <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
            <div>
              <div className="mb-4 flex flex-wrap items-center gap-3">
                <span className={`text-[0.7rem] tracking-[0.08em] ${s.text}`}>{s.stage}</span>
                <span className="text-xs text-text-muted">-</span>
                <span className="text-xs text-text-muted">{s.period}</span>
              </div>
              <h2 className={`mb-4 font-heading text-3xl font-extrabold ${s.text}`}>{s.title}</h2>
              <p className="mb-5 leading-7 text-text-secondary">{s.desc}</p>
              {[
                ['Focus', s.focus, Target],
                ['Capital Source', s.capital, TrendingUp],
              ].map(([label, value, Icon]) => (
                <div key={label as string} className="mb-4 rounded-xl bg-bg-elevated p-4">
                  <p className="mb-1 flex items-center gap-2 text-xs text-text-muted"><Icon size={14} />{label as string}</p>
                  <p className="text-sm text-text-primary">{value as string}</p>
                </div>
              ))}
            </div>

            <div>
              <div className="mb-5 grid gap-4">
                {[
                  { label: 'Plant Cost', value: s.capex },
                  { label: 'Profit Margin', value: s.margin },
                  { label: 'Annual Revenue', value: s.revenue },
                ].map((item) => (
                  <div key={item.label} className="rounded-xl border border-white/10 bg-bg-elevated p-3.5">
                    <p className="mb-1 text-xs text-text-muted">{item.label}</p>
                    <p className={`text-sm font-bold ${s.text}`}>{item.value}</p>
                  </div>
                ))}
              </div>

              <p className="mb-2 flex items-center gap-2 text-xs uppercase tracking-[0.05em] text-text-muted"><Handshake size={14} /> Key Partners</p>
              {s.partners.map((p) => (
                <div key={p} className="mb-2 rounded-lg border border-white/10 bg-bg-elevated px-3.5 py-2 text-[0.8rem] text-text-secondary">
                  {p}
                </div>
              ))}

              <div className={`mt-4 rounded-xl border p-4 ${s.borderSoft} ${s.bgSoft}`}>
                <p className={`mb-1 text-xs ${s.text}`}>OUTCOME</p>
                <p className="text-[0.82rem] leading-6 text-text-primary">{s.outcome}</p>
              </div>
            </div>
          </div>
        </Card>

        <SectionHeader eyebrow="- OVERVIEW TABLE" title="All Four Stages at a Glance" />
        <div className="overflow-x-auto rounded-2xl border border-white/10">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-bg-elevated">
                {['Stage', 'Period', 'Plant Cost', 'Profit Margin', 'Annual Revenue'].map((h) => (
                  <th key={h} className="border-b border-white/10 px-5 py-3.5 text-left font-heading text-[0.78rem] font-bold uppercase tracking-[0.05em] text-text-muted">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stages.map((st, i) => (
                <tr key={st.stage} className={`${i < stages.length - 1 ? 'border-b border-white/10' : ''} ${i % 2 === 0 ? 'bg-bg-card' : 'bg-bg-secondary'}`}>
                  <td className={`px-5 py-4 font-heading font-bold ${st.text}`}>{st.title}</td>
                  <td className="px-5 py-4 text-[0.82rem] text-text-secondary">{st.period}</td>
                  <td className="px-5 py-4 text-[0.82rem] text-text-primary">{st.capex}</td>
                  <td className={`px-5 py-4 text-[0.82rem] ${st.text}`}>{st.margin}</td>
                  <td className={`px-5 py-4 text-[0.82rem] font-bold ${st.text}`}>{st.revenue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Card className="mt-12 border-accent-green/15 bg-gradient-to-br from-accent-green/5 to-violet-400/5">
          <div className="mb-3 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-accent-green/25 bg-accent-green/10 text-accent-green">
              <GraduationCap size={22} />
            </div>
            <h3 className="font-heading text-xl font-bold">Azura EV Academy: Building the Talent Pipeline</h3>
          </div>
          <p className="mb-4 leading-7 text-text-secondary">
            The long-term success of electric mobility in Africa requires local talent. The Azura EV Academy provides structured training in engineering education, battery technology, technical maintenance, and mobility innovation, partnering with universities and global institutions to develop the workforce required at each phase.
          </p>
          <div className="flex flex-wrap gap-4">
            {['Engineering education', 'Battery technology', 'Technical maintenance', 'Mobility innovation', 'University partnerships', 'Global institution links'].map((tag) => (
              <span key={tag} className="rounded-full border border-accent-green/20 bg-accent-green/10 px-3 py-1 text-[0.78rem] text-accent-green">{tag}</span>
            ))}
          </div>
        </Card>

        <Card className="mt-8 border-accent-amber/20 bg-gradient-to-br from-accent-amber/10 to-transparent text-center">
          <h3 className="mb-2 font-heading text-2xl font-bold text-text-primary">Fund the roadmap. <span className="text-accent-amber">Hold a stake in it.</span></h3>
          <p className="mb-6 text-sm leading-6 text-text-secondary max-w-xl mx-auto">AZT ties directly to this manufacturing journey — from rollout revenue today to full local production by 2042. The Strategic Round opens October 2.</p>
          <Link to="/invest" className="inline-flex items-center gap-2 rounded-xl bg-accent-amber px-7 py-3.5 font-heading text-[0.95rem] font-bold text-bg-dark shadow-[0_0_30px_rgba(255,184,0,0.3)] transition hover:-translate-y-1">
            Buy AZT <ArrowRight size={18} />
          </Link>
        </Card>
      </PageWrapper>
    </>
  );
};

export default Manufacturing;
